import { useApi } from '@backstage/core-plugin-api';
import { notificationApiRef } from '../../../apis/notificationApi';
import { toolsProvisioningApiRef } from '../../../apis/toolsProvisioningApi';
import { useRef, useState } from 'react';
import { useUserProfile } from '../../utils/useGetUserProfile';
import { ERROR_TYPE } from '../components/qescore/constants';

export function useQeScoreStream(
  apmNumber: string,
  requestId: string,
  initializeAnalysisResultsForStream: () => void,
  appendEvent: (rawEvent: any) => void,
) {
  const notificationApi = useApi(notificationApiRef);
  const toolsprovisioningApi = useApi(toolsProvisioningApiRef);
  const { accessToken } = useUserProfile();

  const [isStreamingLoading, setIsStreamingLoading] = useState(false);
  const [isTriggering, setIsTriggering] = useState(false);
  const hasActiveWorklogStreamRef = useRef(false);
  const lastProcessedTimestampRef = useRef<string>('');

  const triggerAnalysis = async () => {
    let alreadyInProgress = false;
    try {
      await toolsprovisioningApi.triggerQeScoreAnalysis(apmNumber);
    } catch (triggerError) {
      const errorMessage =
        triggerError instanceof Error
          ? triggerError.message
          : `${triggerError}`;
      if (errorMessage.toLowerCase().includes(ERROR_TYPE.ANALYSIS_TRIGGERED)) {
        // If another user already triggered the analysis for today, display running stream
        alreadyInProgress = true;
        notificationApi.sendNotification({
          message: errorMessage,
          severity: 'info',
          disapperAfterMs: 4000,
        });
      } else {
        throw new Error(errorMessage);
      }
    }
    return alreadyInProgress;
  };

  const streamQeScoreWorkLogs = async (options?: {
    triggerAnalysis?: boolean;
    resetLogs?: boolean;
  }) => {
    const shouldTriggerAnalysis = options?.triggerAnalysis ?? true;
    const resetLogs = options?.resetLogs ?? true;

    if (hasActiveWorklogStreamRef.current) {
      return null;
    }

    hasActiveWorklogStreamRef.current = true;
    setIsStreamingLoading(true);
    setIsTriggering(shouldTriggerAnalysis);

    // For resume: clear previous worklog entries,
    // so they don't flash while the stream connection is being established.
    if (!shouldTriggerAnalysis && !lastProcessedTimestampRef.current) {
      initializeAnalysisResultsForStream();
    }

    if (toolsprovisioningApi) {
      let alreadyInProgress = false;
      try {
        if (shouldTriggerAnalysis) {
          alreadyInProgress = await triggerAnalysis();
        }

        // Open the SSE log stream

        const response = await toolsprovisioningApi.getQeAnalysisWorklogStream(
          apmNumber,
          requestId,
          accessToken,
        );

        if (!response?.body) {
          throw new Error(ERROR_TYPE.NO_STREAM_RESPONSE);
        }

        // Stream is open

        setIsTriggering(false);

        const effectiveResetLogs = resetLogs && !alreadyInProgress;
        if (effectiveResetLogs) {
          // Fresh Analyze click: reset everything
          lastProcessedTimestampRef.current = '';
          initializeAnalysisResultsForStream();
        }

        // Read the stream chunks

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        // Parse each SSE line

        const processLine = (rawLine: string) => {
          const line = rawLine.trim();

          if (!line) {
            return;
          }

          // Skip SSE protocol control lines
          if (
            line.startsWith('event:') ||
            line.startsWith('id:') ||
            line.startsWith('retry:') ||
            line.startsWith(':')
          ) {
            return;
          }

          // All payload lines are prefixed with "data: "
          const jsonStr = line.startsWith('data:')
            ? line.slice(5).trim()
            : line;

          if (!jsonStr) {
            return;
          }

          try {
            appendEvent(JSON.parse(jsonStr));
          } catch (err) {
            // A non-JSON line or unexpected format — log it and continue
            console.error('[QeScore] Failed to parse stream line as JSON', {
              line,
              jsonStr,
              error: err,
            });
          }
        };

        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');

          buffer = lines.pop() || '';

          for (const rawLine of lines) {
            processLine(rawLine);
          }
        }

        // Flush any remaining buffered data

        if (buffer.trim()) {
          processLine(buffer);
        }
      } catch (err) {
        const streamError = err as any;
        const statusCode = streamError?.statusCode;
        const errorMessage =
          err instanceof Error ? err.message : `${err ?? 'Unknown error'}`;

        notificationApi.sendNotification({
          message:
            statusCode === 400
              ? 'Unable to analyze QE Score for this project right now. Please retry again later.'
              : `Error occurred - ${errorMessage}`,
          disapperAfterMs: 4000,
          severity: 'error',
        });

        throw err;
      } finally {
        hasActiveWorklogStreamRef.current = false;
        setIsTriggering(false);
        setIsStreamingLoading(false);
      }
    }
    return null;
  };

  return { isStreamingLoading, isTriggering, streamQeScoreWorkLogs };
}
